import { memo, useEffect, useMemo } from "react"
import type { FC } from "react"

import type { PowerlinePlotDef } from "types/dashboard.types"

import useDateRange from "helpers/hooks/useDateRange"
import { getCellHeight } from "helpers/utils/dashboards"
import type { EntityType } from "types/common.types"
import { useMultiPowerlineTelemetry } from "helpers/hooks/plots/useMultiPowerlineTelemetry"
import { usePlotProcessor } from "helpers/hooks/plots/usePlotProcessor"
import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import Plot from "../Plot"

const PlotMemo = memo(Plot)

interface PowerlineTelemetryPlotProps {
  def: PowerlinePlotDef
  powerlineIds: number[]
}

const PowerlineTelemetryPlot: FC<PowerlineTelemetryPlotProps> = ({
  def,
  powerlineIds,
}) => {
  const { view, reqConfig } = def
  const { config, plotLayout, dispatchPlotLayout, entityType } = useDashboardContext()

  const { getDateRangeTools } = useDateRange()
  const { dateRange, isDateRangeValid } = useMemo(
    () => getDateRangeTools(entityType ?? "powerline"),
    [getDateRangeTools, entityType],
  )

  const { data, qualityData, qualityError, isRawData, error, isFetching, refetch } =
    useMultiPowerlineTelemetry({
      powerlineIds,
      reqConfig,
      dateRange,
      isDateRangeValid,
    })

  const {
    formattedDef,
    tabLegend,
    fieldQualityMap,
    plotData,
    tooltipData,
    shouldRender,
  } = usePlotProcessor({
    data,
    def,
    entityIds: powerlineIds,
    isRawData,
    dateRange,
    error,
  })

  const csvData = useMemo(
    (): {
      entity: EntityType
      entityIds: number[]
      fromDate: string
      toDate: string
    } => ({
      entity: "powerline",
      entityIds: powerlineIds,
      fromDate: String(dateRange?.fromDate),
      toDate: String(dateRange?.toDate),
    }),
    [powerlineIds, dateRange?.fromDate, dateRange?.toDate],
  )

  useEffect(() => {
    if (dispatchPlotLayout && config) {
      dispatchPlotLayout({
        type: "UPDATE_CELL_HEIGHT",
        payload: {
          id: view.nameKey,
          h: shouldRender ? getCellHeight(view.nameKey, config.type) : 0,
        },
      })
    }
  }, [shouldRender, view.nameKey, dispatchPlotLayout, config])

  const render = Boolean(
    shouldRender && (plotLayout === null || plotLayout?.[view.nameKey]?.h !== 0),
  )

  return (
    <>
      {render && (
        <PlotMemo
          data={plotData}
          def={formattedDef}
          csvData={csvData}
          tooltipData={tooltipData}
          entityData={{ entity: "powerline", ids: powerlineIds }}
          qualityData={qualityData}
          qualityError={qualityError}
          isRawData={isRawData}
          seriesLegend={tabLegend}
          fieldQualityMap={fieldQualityMap}
          error={error}
          isFetching={isFetching}
          refetch={refetch}
        />
      )}
    </>
  )
}

export default PowerlineTelemetryPlot
